import { useState, useEffect, useCallback, useMemo } from "react";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Receipt, DownloadSimple, Check, Bank, CaretDown, CaretUp, Trash, ArrowLeft, LinkSimple,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import BulkActionBar from "@/components/app/BulkActionBar";

function gbp(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);
}

export default function InvoicesPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletedInvoices, setDeletedInvoices] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [invRes, refRes, delRes] = await Promise.all([
        API.get("/invoices"),
        API.get("/referrers"),
        API.get("/invoices/deleted").catch(() => ({ data: [] })),
      ]);
      setInvoices(invRes.data);
      setReferrers(refRes.data);
      setDeletedInvoices(delRes.data || []);
    } catch {
      toast.error("Failed to load invoices");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const groups = useMemo(() => {
    const refMap = new Map(referrers.map((r) => [r.id, r]));
    const byRef = new Map();
    invoices.forEach((inv) => {
      const k = inv.referrer_id || "unknown";
      if (!byRef.has(k)) byRef.set(k, []);
      byRef.get(k).push(inv);
    });
    return Array.from(byRef.entries()).map(([rid, list]) => {
      const r = refMap.get(rid) || { id: rid, name: "Unknown", company: "" };
      const unpaid = list.filter((i) => i.status !== "paid").reduce((s, i) => s + (i.amount_gross || 0), 0);
      const paid = list.filter((i) => i.status === "paid").reduce((s, i) => s + (i.amount_gross || 0), 0);
      return {
        referrer: r,
        invoices: list.sort((a, b) => (a.status === "paid") - (b.status === "paid") || b.issued_at.localeCompare(a.issued_at)),
        unpaid_total: unpaid,
        paid_total: paid,
        total: unpaid + paid,
      };
    }).sort((a, b) => b.unpaid_total - a.unpaid_total || a.referrer.name.localeCompare(b.referrer.name));
  }, [invoices, referrers]);

  const grand = useMemo(() => ({
    unpaid: groups.reduce((s, g) => s + g.unpaid_total, 0),
    paid: groups.reduce((s, g) => s + g.paid_total, 0),
    total: groups.reduce((s, g) => s + g.total, 0),
  }), [groups]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleGroupUnpaid = (g) => {
    const next = new Set(selected);
    const ids = g.invoices.filter((i) => i.status !== "paid").map((i) => i.id);
    const allSelected = ids.every((id) => next.has(id));
    ids.forEach((id) => allSelected ? next.delete(id) : next.add(id));
    setSelected(next);
  };

  const handleBatchPaid = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await API.post("/invoices/batch-paid", { invoice_ids: Array.from(selected), paid: true });
      toast.success(`${data.updated} invoice${data.updated === 1 ? "" : "s"} marked paid`);
      setSelected(new Set());
      fetchAll();
    } catch {
      toast.error("Failed to mark invoices paid");
    } finally { setBusy(false); }
  };

  const handleStatementPdf = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const resp = await API.post("/invoices/statement-pdf", { invoice_ids: Array.from(selected) }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch {
      toast.error("Failed to download statement");
    } finally { setBusy(false); }
  };

  const handleStatementCsv = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const resp = await API.post("/invoices/statement-csv", { invoice_ids: Array.from(selected) }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Failed to download CSV");
    } finally { setBusy(false); }
  };

  const handleSingleDownload = async (inv) => {
    try {
      const resp = await API.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download invoice"); }
  };

  const handleCopyMagicLink = async (inv) => {
    /* If the invoice already has a token, build the link directly.
       Otherwise (legacy invoice pre-magic-link), ask the backend to
       mint one — the rotate endpoint covers both cases. */
    try {
      let token = inv.magic_link_token;
      if (!token) {
        const { data } = await API.post(`/invoices/${inv.id}/rotate-magic-link`);
        token = data.magic_link_token;
      }
      const link = `${window.location.origin}/api/invoices/public/${token}`;
      await navigator.clipboard.writeText(link);
      toast.success("Magic link copied to clipboard");
    } catch (err) {
      toast.error("Failed to create magic link");
    }
  };

  const handleSoftDeleteInvoice = async (inv) => {
    if (!window.confirm(`Move invoice ${inv.invoice_number} to Recently Deleted?`)) return;
    try {
      await API.delete(`/invoices/${inv.id}`);
      toast.success("Invoice moved to Recently Deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleRestoreInvoice = async (id) => {
    try {
      await API.post(`/invoices/${id}/restore`);
      toast.success("Invoice restored");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleEmptyInvoiceTrash = async () => {
    if (deletedInvoices.length === 0) return;
    const c = window.prompt(
      `Permanently delete ALL ${deletedInvoices.length} invoices in Recently Deleted?\n\n⚠️ Invoices are HMRC audit records. Only do this for genuinely erroneous entries.\n\nType DELETE to confirm:`
    );
    if (c !== "DELETE") { toast.info("Cancelled"); return; }
    try {
      const { data } = await API.delete("/invoices/deleted/empty");
      toast.success(`Permanently deleted ${data.purged} invoice${data.purged === 1 ? "" : "s"}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // ---------- Bulk delete/restore/purge ----------
  const [selectedTrash, setSelectedTrash] = useState(new Set());
  const toggleTrash = (id) => {
    const n = new Set(selectedTrash); n.has(id) ? n.delete(id) : n.add(id); setSelectedTrash(n);
  };
  const handleBulkSoftDeleteInv = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Move ${selected.size} invoice${selected.size === 1 ? "" : "s"} to Recently Deleted?`)) return;
    try {
      const { data } = await API.post("/invoices/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} moved to Recently Deleted`);
      setSelected(new Set());
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };
  const handleBulkRestoreInv = async () => {
    if (!selectedTrash.size) return;
    try {
      const { data } = await API.post("/invoices/bulk-restore", { ids: Array.from(selectedTrash) });
      toast.success(`${data.restored} restored`);
      setSelectedTrash(new Set());
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk restore failed");
    }
  };
  const handleBulkPurgeInv = async () => {
    if (!selectedTrash.size) return;
    const c = window.prompt(`Permanently delete ${selectedTrash.size} selected invoice${selectedTrash.size === 1 ? "" : "s"}?\n\n⚠️ HMRC audit records. Type DELETE to confirm:`);
    if (c !== "DELETE") return;
    try {
      const { data } = await API.post("/invoices/bulk-permanent-delete", { ids: Array.from(selectedTrash) });
      toast.success(`${data.purged} permanently deleted`);
      setSelectedTrash(new Set());
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk purge failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="invoices-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Referral Invoices</h1>
            <p className="text-sm text-slate-500 mt-1">Auto-generated when cases complete. Group by referrer for batch payouts.</p>
          </div>
          <Button onClick={() => setViewDeleted(!viewDeleted)} variant="outline" className="h-9 text-sm gap-1.5" data-testid="toggle-deleted-invoices-btn">
            {viewDeleted ? <><ArrowLeft weight="bold" className="w-4 h-4" /> Back to active</> : <><Trash weight="bold" className="w-4 h-4" /> Recently Deleted{deletedInvoices.length > 0 ? ` (${deletedInvoices.length})` : ""}</>}
          </Button>
        </div>

        {/* Recently Deleted view */}
        {viewDeleted ? (
          <div data-testid="deleted-invoices-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs text-slate-500 font-medium">
                ⚠️ Invoices are HMRC audit records. Restore wherever possible — only purge erroneous entries.
              </p>
              {user?.is_admin && deletedInvoices.length > 0 && (
                <Button onClick={handleEmptyInvoiceTrash} variant="outline" size="sm"
                  className="h-8 text-xs gap-1.5 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="empty-invoices-trash-btn">
                  <Trash weight="bold" className="w-3.5 h-3.5" /> Empty Trash ({deletedInvoices.length})
                </Button>
              )}
            </div>
            {deletedInvoices.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="empty-deleted-invoices">
                <Trash weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 font-medium">No invoices in Recently Deleted</p>
              </div>
            ) : (
              <>
              <BulkActionBar
                count={selectedTrash.size}
                onClear={() => setSelectedTrash(new Set())}
                onRestore={handleBulkRestoreInv}
                onPurge={user?.is_admin ? handleBulkPurgeInv : undefined}
                label="invoice"
                variant="trash"
                testIdPrefix="invoices-trash"
              />
              <div className="space-y-2">
                {deletedInvoices.map((inv) => {
                  const isSel = selectedTrash.has(inv.id);
                  return (
                  <div key={inv.id} className={`bg-white border rounded-lg p-4 shadow-sm flex items-center gap-3 ${isSel ? "border-[#100f5d] ring-2 ring-[#100f5d]/15" : "border-red-100 opacity-80 hover:opacity-100"}`} data-testid={`deleted-inv-row-${inv.id}`}>
                    <Checkbox checked={isSel} onCheckedChange={() => toggleTrash(inv.id)} data-testid={`deleted-inv-cb-${inv.id}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-mono font-semibold text-slate-900">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-500 truncate">{inv.case_title} · Issued {new Date(inv.issued_at).toLocaleDateString("en-GB")}</p>
                      <p className="text-[11px] text-slate-400 mt-1">Deleted {inv.deleted_at ? new Date(inv.deleted_at).toLocaleString("en-GB") : ""}</p>
                    </div>
                    <p className="text-sm font-bold text-slate-900 shrink-0">{gbp(inv.amount_gross)}</p>
                    <Button onClick={() => handleRestoreInvoice(inv.id)} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid={`restore-inv-${inv.id}`}>
                      <ArrowLeft weight="bold" className="w-3.5 h-3.5" /> Restore
                    </Button>
                  </div>
                  );
                })}
              </div>
              </>
            )}
          </div>
        ) : (
        <>
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white border border-amber-200 rounded-lg p-4">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Awaiting Payment</p>
            <p className="text-2xl font-bold text-amber-700 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="grand-unpaid">{gbp(grand.unpaid)}</p>
          </div>
          <div className="bg-white border border-emerald-200 rounded-lg p-4">
            <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">Paid</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="grand-paid">{gbp(grand.paid)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Total Issued</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="grand-total">{gbp(grand.total)}</p>
          </div>
        </div>

        {/* Selection action bar */}
        {selected.size > 0 && (
          <div className="sticky top-2 z-10 bg-slate-900 text-white rounded-lg px-4 py-3 mb-4 flex items-center justify-between shadow-lg" data-testid="batch-action-bar">
            <span className="text-sm font-medium">{selected.size} invoice{selected.size === 1 ? "" : "s"} selected</span>
            <div className="flex items-center gap-2">
              <Button onClick={() => setSelected(new Set())} variant="ghost" size="sm" className="text-white hover:bg-slate-800 h-8 text-xs">Clear</Button>
              <Button onClick={handleStatementCsv} disabled={busy} size="sm" className="h-8 text-xs bg-white text-slate-900 hover:bg-slate-100" data-testid="statement-csv-btn">
                <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
              <Button onClick={handleStatementPdf} disabled={busy} size="sm" className="h-8 text-xs bg-white text-slate-900 hover:bg-slate-100" data-testid="statement-pdf-btn">
                <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1" /> Statement PDF
              </Button>
              <Button onClick={handleBatchPaid} disabled={busy} size="sm" className="h-8 text-xs bg-emerald-600 text-white hover:bg-emerald-700" data-testid="batch-paid-btn">
                <Check weight="bold" className="w-3.5 h-3.5 mr-1" /> Mark {selected.size} as Paid
              </Button>
              <Button onClick={handleBulkSoftDeleteInv} disabled={busy} size="sm" className="h-8 text-xs bg-red-500 text-white hover:bg-red-600" data-testid="batch-delete-inv-btn">
                <Trash weight="bold" className="w-3.5 h-3.5 mr-1" /> Delete {selected.size}
              </Button>
            </div>
          </div>
        )}

        {/* Groups */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" /></div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
            <Receipt weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No invoices yet</p>
            <p className="text-xs text-slate-400 mt-1">Invoices are auto-generated when a case completes.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const isCollapsed = collapsed[g.referrer.id];
              const unpaidIds = g.invoices.filter((i) => i.status !== "paid").map((i) => i.id);
              const allUnpaidSelected = unpaidIds.length > 0 && unpaidIds.every((id) => selected.has(id));
              return (
                <div key={g.referrer.id} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid={`group-${g.referrer.id}`}>
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                    <button onClick={() => setCollapsed({ ...collapsed, [g.referrer.id]: !isCollapsed })} className="flex items-center gap-3 min-w-0">
                      {isCollapsed ? <CaretDown className="w-4 h-4 text-slate-400" /> : <CaretUp className="w-4 h-4 text-slate-400" />}
                      <div className="text-left min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{g.referrer.name}{g.referrer.company ? ` · ${g.referrer.company}` : ""}</p>
                        <p className="text-xs text-slate-500">{g.invoices.length} invoice{g.invoices.length === 1 ? "" : "s"}</p>
                      </div>
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-amber-600">Unpaid: <span className="font-bold">{gbp(g.unpaid_total)}</span></p>
                        <p className="text-[10px] text-slate-400">Paid: {gbp(g.paid_total)}</p>
                      </div>
                      {unpaidIds.length > 0 && (
                        <Button onClick={() => toggleGroupUnpaid(g)} size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`select-unpaid-${g.referrer.id}`}>
                          {allUnpaidSelected ? "Deselect" : "Select"} unpaid
                        </Button>
                      )}
                    </div>
                  </div>
                  {!isCollapsed && (
                    <div className="divide-y divide-slate-100">
                      {/* Bank details strip (top of group) */}
                      {(g.referrer.bank_name || g.referrer.account_number) && (
                        <div className="px-4 py-2.5 bg-violet-50 text-[11px] flex items-center gap-2 flex-wrap">
                          <Bank weight="fill" className="w-3.5 h-3.5 text-violet-600 shrink-0" />
                          {g.referrer.bank_name && <span><span className="text-slate-500">Bank:</span> <span className="font-medium">{g.referrer.bank_name}</span></span>}
                          {g.referrer.sort_code && <span><span className="text-slate-500">Sort:</span> <span className="font-mono">{g.referrer.sort_code}</span></span>}
                          {g.referrer.account_number && <span><span className="text-slate-500">Acc:</span> <span className="font-mono">{g.referrer.account_number}</span></span>}
                          {g.referrer.account_name && <span><span className="text-slate-500">Name:</span> <span className="font-medium">{g.referrer.account_name}</span></span>}
                          {g.referrer.vat_number && <span><span className="text-slate-500">VAT:</span> <span className="font-mono">{g.referrer.vat_number}</span></span>}
                        </div>
                      )}
                      {g.invoices.map((inv) => (
                        <div key={inv.id} className="px-4 py-3 flex items-center gap-3" data-testid={`inv-row-${inv.id}`}>
                          <Checkbox checked={selected.has(inv.id)} onCheckedChange={() => toggleOne(inv.id)} data-testid={`select-inv-${inv.id}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-mono font-semibold text-slate-900">{inv.invoice_number}</p>
                            <p className="text-xs text-slate-500 truncate">{inv.case_title} · Issued {new Date(inv.issued_at).toLocaleDateString("en-GB")}</p>
                            {inv.legal_fee_total != null && (
                              <p className="text-[11px] text-slate-400 truncate">Case legal fee: {gbp(inv.legal_fee_total)}{inv.legal_fees_with_vat != null && <span className="text-slate-300"> · {gbp(inv.legal_fees_with_vat)} inc VAT</span>}</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-slate-900">{gbp(inv.amount_gross)}</p>
                            {inv.vat_applied && <p className="text-[10px] text-slate-400">incl. VAT</p>}
                          </div>
                          <span className={`text-[10px] font-medium px-2 py-1 rounded-md shrink-0 ${
                            inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                          }`}>
                            {inv.status === "paid" ? `Paid ${inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}` : "Issued"}
                          </span>
                          <button onClick={() => handleCopyMagicLink(inv)} data-testid={`copy-magic-link-${inv.id}`}
                            title="Copy public share link"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 transition-colors shrink-0">
                            <LinkSimple weight="bold" className="w-3.5 h-3.5" /> Link
                          </button>
                          <button onClick={() => handleSingleDownload(inv)} data-testid={`download-${inv.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 transition-colors shrink-0">
                            <DownloadSimple weight="bold" className="w-3.5 h-3.5" /> PDF
                          </button>
                          <button onClick={() => handleSoftDeleteInvoice(inv)} data-testid={`delete-inv-${inv.id}`}
                            title="Move to Recently Deleted"
                            className="inline-flex items-center justify-center p-1 rounded-md hover:bg-red-50 transition-colors shrink-0">
                            <Trash weight="bold" className="w-3.5 h-3.5 text-slate-400 hover:text-red-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
